import { useChatInputStore, useCoworkerStore, useModelStore } from '@/store';
import { blockIfWorkModeDisabled } from '@/lib/workModeAvailability';
import { COMPOSER_DRAFT_MAX_CHARS, readComposerDraft, saveComposerDraft } from '@/lib/composerDraft';
import { selectUseCaseModel } from '@/lib/useCaseModelSelection';

const KEY = 'gab-use-case-draft';
const MAX_AGE = 30 * 60 * 1000;
let stopRetainingEdits = null;

export function clearUseCaseDraftHandoff() {
  stopRetainingEdits?.();
  stopRetainingEdits = null;
  try {
    if (typeof sessionStorage !== 'undefined') sessionStorage.removeItem(KEY);
  } catch (_) { /* Storage may be unavailable. */ }
}

function retainComposerEdits() {
  stopRetainingEdits?.();
  stopRetainingEdits = useChatInputStore.subscribe((state, previous) => {
    const text = state.inputStates.new?.text;
    const selected = state.inputStates.new?.selectedModelIds || [];
    const prior = previous.inputStates.new?.selectedModelIds || [];
    const modelChanged = JSON.stringify(selected) !== JSON.stringify(prior);
    if (typeof text !== 'string' || (text === previous.inputStates.new?.text && !modelChanged)) return;
    try {
      const raw = sessionStorage.getItem(KEY);
      if (!raw) return;
      const draft = JSON.parse(raw);
      if (modelChanged && draft.modelSlug) {
        const arya = useModelStore.getState().getModelBySlug('arya');
        if (selected.length !== 1 || selected[0] !== arya?.id) delete draft.modelSlug;
      }
      if (Date.now() - draft.createdAt > MAX_AGE || text.length > COMPOSER_DRAFT_MAX_CHARS) {
        clearUseCaseDraftHandoff();
        return;
      }
      // Preserve an intentional empty draft as well as edits through reload.
      sessionStorage.setItem(KEY, JSON.stringify({ ...draft, editedText: text }));
    } catch (_) { /* The composer keeps working if storage is unavailable. */ }
  });
}

// Only the explicitly selected public example is retained. No source files,
// account records, or automatic-send instructions enter this handoff.
export function retainUseCaseDraft(prompt, mode, modelSlug) {
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(KEY, JSON.stringify({ prompt, mode, ...(modelSlug === 'arya' && mode === 'chat' ? { modelSlug } : {}), createdAt: Date.now() }));
      saveComposerDraft('new', prompt);
      retainComposerEdits();
    }
  } catch (_) { /* The in-memory composer still works when storage is unavailable. */ }
}

export function restoreUseCaseDraft(isAuthenticated) {
  try {
    if (typeof sessionStorage === 'undefined') return false;
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return false;
    const draft = JSON.parse(raw);
    if (!draft || typeof draft.prompt !== 'string' || !draft.prompt.trim() || draft.prompt.length > 10000
      || !['chat','work'].includes(draft.mode) || !Number.isFinite(draft.createdAt)
      || (draft.modelSlug != null && (draft.modelSlug !== 'arya' || draft.mode !== 'chat'))
      || (draft.editedText != null && (typeof draft.editedText !== 'string' || draft.editedText.length > COMPOSER_DRAFT_MAX_CHARS))
      || draft.createdAt > Date.now() || Date.now() - draft.createdAt > MAX_AGE) {
      clearUseCaseDraftHandoff();
      return false;
    }
    if (draft.mode === 'work' && blockIfWorkModeDisabled()) {
      clearUseCaseDraftHandoff();
      return false;
    }
    const composer = useChatInputStore.getState();
    // Web initialization can create an empty context before this effect runs.
    // Trust that context only after this page has retained/restored the handoff.
    const existingText = composer.inputStates.new?.text;
    const text = stopRetainingEdits && typeof existingText === 'string' ? existingText
      : typeof draft.editedText === 'string' ? draft.editedText : readComposerDraft('new') || draft.prompt;
    composer.setContext('new');
    composer.clearPendingAutoSend();
    composer.setText(text);
    useCoworkerStore.getState().setComposerMode(draft.mode === 'work' ? 'coworker' : 'chat');
    selectUseCaseModel(draft.modelSlug);
    if (isAuthenticated) clearUseCaseDraftHandoff();
    else retainComposerEdits();
    return true;
  } catch (_) {
    clearUseCaseDraftHandoff();
    return false;
  }
}
